import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  AppBar,
  Toolbar as MuiToolbar,
} from "@mui/material";
import {
  Save as SaveIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  ArrowBack as ArrowBackIcon,
  GridOn as GridOnIcon,
  GridOff as GridOffIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  setCurrentWarehouse,
  setElements,
  setZoom,
  undo,
  redo,
  toggleShowGrid,
  toggleSnapToGrid,
  markAsSaved,
} from "../../store/warehouseSlice";
import * as warehouseApi from "./api";
import GridCanvas from "./components/GridCanvas";
import Toolbar from "./components/Toolbar";
import PropertiesPanel from "./components/PropertiesPanel";

const WarehouseDesigner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    currentWarehouse,
    elements,
    zoom,
    showGrid,
    snapToGrid,
    hasUnsavedChanges,
    history,
  } = useSelector((state) => state.warehouse);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadWarehouse();
    }
  }, [id]);

  const loadWarehouse = async () => {
    try {
      setLoading(true);
      const warehouseResponse = await warehouseApi.getWarehouseById(id);
      if (warehouseResponse.success) {
        dispatch(setCurrentWarehouse(warehouseResponse.data));
      }

      // Load layout with zones and racks
      const layoutResponse = await warehouseApi.getWarehouseLayout(id);
      if (layoutResponse.success) {
        const { zones, racks } = layoutResponse.data;

        // Convert zones and racks to canvas elements
        const zoneElements = zones.map((zone) => ({
          id: zone._id,
          type: "zone",
          name: zone.name,
          code: zone.code,
          color: zone.color,
          zoneType: zone.zoneType,
          gridPosition: zone.gridPosition,
          data: zone,
        }));

        const rackElements = racks.map((rack) => ({
          id: rack._id,
          type: "rack",
          name: rack.name,
          code: rack.code,
          rackType: rack.rackType,
          gridPosition: rack.gridPosition,
          rotation: rack.rotation || 0,
          numberOfShelves: rack.numberOfShelves,
          data: rack,
        }));

        dispatch(setElements([...zoneElements, ...rackElements]));
      }
    } catch (error) {
      console.error("Error loading warehouse:", error);
      toast.error("Fehler beim Laden des Lagers");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Save zones
      const zoneElements = elements.filter((e) => e.type === "zone");
      for (const zone of zoneElements) {
        if (zone.data?._id) {
          // Update existing
          await warehouseApi.updateZone(zone.data._id, {
            name: zone.name,
            code: zone.code,
            color: zone.color,
            zoneType: zone.zoneType,
            gridPosition: zone.gridPosition,
          });
        } else {
          // Create new
          await warehouseApi.createZone({
            warehouse: id,
            name: zone.name,
            code: zone.code,
            color: zone.color,
            zoneType: zone.zoneType,
            gridPosition: zone.gridPosition,
          });
        }
      }

      // Save racks
      const rackElements = elements.filter((e) => e.type === "rack");
      for (const rack of rackElements) {
        if (rack.data?._id) {
          // Update existing
          await warehouseApi.updateRack(rack.data._id, {
            name: rack.name,
            code: rack.code,
            rackType: rack.rackType,
            gridPosition: rack.gridPosition,
            rotation: rack.rotation,
            numberOfShelves: rack.numberOfShelves,
          });
        } else {
          // Create new
          await warehouseApi.createRack({
            warehouse: id,
            name: rack.name,
            code: rack.code,
            rackType: rack.rackType,
            gridPosition: rack.gridPosition,
            rotation: rack.rotation,
            numberOfShelves: rack.numberOfShelves,
            shelfConfiguration: Array.from({ length: rack.numberOfShelves }, (_, i) => ({
              level: i + 1,
              height: 1,
              positions: 10,
            })),
          });
        }
      }

      // Save layout to warehouse
      await warehouseApi.saveWarehouseLayout(id, {
        elements,
        lastModified: new Date().toISOString(),
      });

      dispatch(markAsSaved());
      toast.success("Lagerlayout erfolgreich gespeichert");
    } catch (error) {
      console.error("Error saving warehouse:", error);
      toast.error(error.message || "Fehler beim Speichern des Lagerlayouts");
    }
  };

  const handleZoomIn = () => {
    dispatch(setZoom(Math.min(zoom + 0.1, 5)));
  };

  const handleZoomOut = () => {
    dispatch(setZoom(Math.max(zoom - 0.1, 0.1)));
  };

  const handleFitScreen = () => {
    dispatch(setZoom(1));
  };

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Lade Lagerdesigner...</Typography>
      </Box>
    );
  }

  if (!currentWarehouse) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Lager nicht gefunden</Typography>
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        {/* Top Toolbar */}
        <AppBar position="static" sx={{ backgroundColor: "#2d3748" }}>
          <MuiToolbar>
            <IconButton color="inherit" onClick={() => navigate("/warehouse")} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {currentWarehouse.name} - Designer
              {hasUnsavedChanges && <span style={{ color: "#f39c12", marginLeft: 8 }}>●</span>}
            </Typography>

            {/* Undo/Redo */}
            <Tooltip title="Rückgängig">
              <span>
                <IconButton color="inherit" onClick={() => dispatch(undo())} disabled={!canUndo}>
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Wiederholen">
              <span>
                <IconButton color="inherit" onClick={() => dispatch(redo())} disabled={!canRedo}>
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: "#ffffff33" }} />

            {/* Zoom Controls */}
            <Tooltip title="Verkleinern">
              <IconButton color="inherit" onClick={handleZoomOut}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Typography sx={{ mx: 1, minWidth: 50, textAlign: "center" }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Vergrößern">
              <IconButton color="inherit" onClick={handleZoomIn}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="An Bildschirm anpassen">
              <IconButton color="inherit" onClick={handleFitScreen}>
                <FitScreenIcon />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: "#ffffff33" }} />

            {/* Grid Controls */}
            <Tooltip title={showGrid ? "Raster ausblenden" : "Raster einblenden"}>
              <IconButton color="inherit" onClick={() => dispatch(toggleShowGrid())}>
                {showGrid ? <GridOnIcon /> : <GridOffIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={snapToGrid ? "Rasterausrichtung deaktivieren" : "Rasterausrichtung aktivieren"}>
              <IconButton
                color="inherit"
                onClick={() => dispatch(toggleSnapToGrid())}
                sx={{ color: snapToGrid ? "#4361ee" : "inherit" }}
              >
                <GridOnIcon />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: "#ffffff33" }} />

            {/* Save Button */}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{
                backgroundColor: "#2ecc71",
                "&:hover": { backgroundColor: "#27ae60" },
                ml: 1,
              }}
            >
              Speichern
            </Button>
          </MuiToolbar>
        </AppBar>

        {/* Main Content Area */}
        <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
          {/* Left Toolbar */}
          <Box sx={{ width: 280, borderRight: "1px solid #e0e0e0", overflowY: "auto" }}>
            <Toolbar />
          </Box>

          {/* Center Canvas */}
          <Box sx={{ flexGrow: 1, position: "relative", overflow: "hidden", backgroundColor: "#f5f5f5" }}>
            <GridCanvas />
          </Box>

          {/* Right Properties Panel */}
          <Box sx={{ width: 320, borderLeft: "1px solid #e0e0e0", overflowY: "auto" }}>
            <PropertiesPanel />
          </Box>
        </Box>
      </Box>

      <ToastContainer position="bottom-right" autoClose={3000} />
    </DndProvider>
  );
};

export default WarehouseDesigner;
